import { Metadata } from 'next'
import { getLocale } from 'next-intl/server'

import { RoomCount } from '@/entities/map/@x/room'

import { getPropertyTypeMap } from '../../model/option'
import { PropertyType } from '../../model/room'

const KO_ROOM_COUNT_LABEL: Record<RoomCount, string> = {
  [RoomCount.One]: '원룸',
  [RoomCount.Two]: '투룸',
  [RoomCount.ThreePlus]: '쓰리룸 이상',
}

const EN_ROOM_COUNT_LABEL: Record<RoomCount, string> = {
  [RoomCount.One]: 'One',
  [RoomCount.Two]: 'Two',
  [RoomCount.ThreePlus]: 'Three',
}

const KO_ROOM_LIST_TEMPLATES = {
  withRoomCount: (
    keyword: string | undefined,
    typeNames: string | undefined,
    roomCount: string,
  ) => {
    const parts = [keyword, typeNames, roomCount].filter(Boolean).join(' ')

    return {
      title: `${parts} 단기임대, 지금 계약 가능한 잠깐 살 집 찾기 | 삼삼엠투`,
      description: `${parts} 단기임대 매물 보기. 사진, 가격, 실시간 후기, 방 개수, 옵션, 주차 정보를 지금 바로 확인하세요.`,
    }
  },
  keywordType: (keyword: string, typeNames: string) => ({
    title: `${keyword} ${typeNames} 단기임대, 즉시 입주 가능한 잠깐 살 집 찾기 | 삼삼엠투`,
    description: `${keyword} ${typeNames} 단기임대 매물 보기. 사진, 가격, 실시간 후기, 방 개수, 옵션, 주차 정보를 지금 바로 확인하세요.`,
  }),
  keyword: (keyword: string) => ({
    title: `${keyword} 단기임대, 지금 계약 가능한 잠깐 살 집 찾기 | 삼삼엠투`,
    description: `${keyword} 근처 단기임대 매물 보기. 사진, 가격, 실시간 후기, 방 개수, 옵션, 주차 정보를 지금 바로 확인하세요.`,
  }),
  type: (typeNames: string) => ({
    title: `${typeNames} 단기임대, 내 집처럼 편안한 잠깐 살 집 찾기 | 삼삼엠투`,
    description: `${typeNames} 단기임대 매물 보기. 사진, 가격, 실시간 후기, 방 개수, 옵션, 주차 정보를 지금 바로 확인하세요.`,
  }),
}

const EN_ROOM_LIST_TEMPLATES = {
  withRoomCount: (
    keyword: string | undefined,
    typeNames: string | undefined,
    roomCount: string,
  ) => {
    const roomCountLower = roomCount.replace(/-Room/g, '-room')

    if (keyword && !typeNames) {
      return {
        title: `${roomCount} Leases in ${keyword} | 33m2`,
        description: `Browse ${roomCountLower} lease listings in ${keyword} on 33m2. Compare pricing, layouts, and terms to find the right place for your stay.`,
      }
    }

    if (!keyword && typeNames) {
      return {
        title: `${roomCount} ${typeNames} Leases | 33m2`,
        description: `Browse ${roomCountLower} ${typeNames} lease listings on 33m2. Compare pricing, layouts, and lease terms to find the right place for your stay.`,
      }
    }

    const subject = [roomCount, typeNames].filter(Boolean).join(' ')

    return {
      title: `${subject}${keyword ? ` in ${keyword}` : ' Leases'} | 33m2`,
      description: `Browse ${subject} listings${keyword ? ` in ${keyword}` : ''} on 33m2. Compare pricing, layout, and lease terms to find the right place for the period you need.`,
    }
  },
  keywordType: (keyword: string, typeNames: string) => ({
    title: `${typeNames} Short-Term Lease in ${keyword} | 33m2`,
    description: `Explore ${typeNames} listings in ${keyword} for short-term lease on 33m2. Compare price, room count, and lease period to find the right place for the period you need.`,
  }),
  keyword: (keyword: string) => ({
    title: `Short-Term Leases in ${keyword} | 33m2`,
    description: `Explore short-term lease homes in ${keyword} on 33m2. Compare officetels, apartments, and studios for business trips, relocation, renovation, study, or medical visits.`,
  }),
  type: (typeNames: string) => ({
    title: `${typeNames} Short-Term Lease | 33m2`,
    description: `Explore ${typeNames} listings available for short-term lease on 33m2. Filter by region, price, and room count to find the right place in Korea.`,
  }),
}

const withOpenGraph = ({
  title,
  description,
}: {
  title: string
  description: string
}): Metadata => ({
  title,
  description,
  openGraph: {
    title,
    description,
    images: [`${process.env.NEXT_PUBLIC_URL}/image/og-image.png`],
  },
})

export const generateRoomListMetadata = async (
  keyword: string | undefined,
  propertyTypes?: PropertyType[],
  roomCounts?: RoomCount[],
): Promise<Metadata> => {
  const hasPropertyTypes = !!propertyTypes?.length
  const hasRoomCounts = !!roomCounts?.length

  if (!keyword && !hasPropertyTypes && !hasRoomCounts) {
    const locale = await getLocale()

    return locale === 'en'
      ? withOpenGraph({
          title: 'Short-Term Lease Homes in Korea | 33m2',
          description:
            'Browse short-term lease homes across Korea on 33m2. Filter by region, property type, room count, and price to find the right home for a while.',
        })
      : withOpenGraph({
          title: '삼삼엠투 | 내 집처럼 편안한 단기임대 집 찾기',
          description:
            '원룸부터 오피스텔, 아파트까지. 단기임대 1위, 믿을 수 있는 삼삼엠투에서 안전하고 편리하게 잠깐 살 집을 찾아보세요.',
        })
  }

  const [{ PROPERTY_TYPE_MAP }, locale] = await Promise.all([
    getPropertyTypeMap(),
    getLocale(),
  ])

  const isEn = locale === 'en'
  const t = isEn ? EN_ROOM_LIST_TEMPLATES : KO_ROOM_LIST_TEMPLATES

  const typeNames = hasPropertyTypes
    ? propertyTypes.map(p => PROPERTY_TYPE_MAP[p]).join(', ')
    : undefined

  const roomCountLabel = hasRoomCounts
    ? roomCounts
        .map(r =>
          isEn ? `${EN_ROOM_COUNT_LABEL[r]}-Room` : KO_ROOM_COUNT_LABEL[r],
        )
        .join(', ')
    : undefined

  if (roomCountLabel) {
    return withOpenGraph(t.withRoomCount(keyword, typeNames, roomCountLabel))
  }

  if (keyword && typeNames) {
    return withOpenGraph(t.keywordType(keyword, typeNames))
  }

  if (keyword) {
    return withOpenGraph(t.keyword(keyword))
  }

  return withOpenGraph(t.type(typeNames!))
}
